import {
  Box,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import CustomPasswordInput from "../../../components/PasswordInput";
import { useFormik } from "formik";
import { toast, Toaster } from "react-hot-toast";
import { useEffect, useState, useMemo } from "react";
import { decryptToken } from "../../../Utlis/Encryption";
import { BASE_URL } from "../../../components/BaseUrl";
import { useTranslation } from 'react-i18next';
import { showToast } from "../../../Utlis/messageHandler";
/* -------------------- RULE ICON --------------------- */
const RuleRow = ({ ok, label, showError }) => {
  const textColor = ok
    ? '#16a34a'
    : showError
      ? '#d32f2f'
      : '#64748b';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: ok ? 'none' : '2px solid #9aa4b2',
          background: ok ? '#22c55e' : 'transparent'
        }}
      >
        {ok ? (
          <span
            style={{
              width: 6,
              height: 10,
              borderRight: '2px solid #fff',
              borderBottom: '2px solid #fff',
              transform: 'rotate(45deg)',
            }}
          />
        ) : (
          <span style={{ position: 'relative', width: 10, height: 10 }}>
            <span
              style={{
                position: 'absolute',
                width: '100%',
                height: 2,
                background:'#9aa4b2',
                transform: 'rotate(45deg)',
                top: '50%',
                left: 0,
                marginTop: -1,
              }}
            />
            <span
              style={{
                position: 'absolute',
                width: '100%',
                height: 2,
                background:  '#9aa4b2',
                transform: 'rotate(-45deg)',
                top: '50%',
                left: 0,
                marginTop: -1,
              }}
            />
          </span>
        )}
      </span>
      <span style={{ fontSize: 14, color: textColor }}>{label}</span>
    </Box>
  );
};

/* -------------------- PASSWORD CHECKLIST --------------------- */
const PasswordChecklist = ({ password, showError }) => {
    const { t } = useTranslation();
  const rules = useMemo(() => ({
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()[\]{}\-_+=~`|:;"'<>,.?/\\]/.test(password),
  }), [password]);

  return (
<Box sx={{ mt: 1, mb: { xs: 2, sm: 1.5 } }}>
  <RuleRow ok={rules.length} label={t('passwordRules.minLength')} showError={showError && !rules.length}/>
  <RuleRow ok={rules.upper} label={t('passwordRules.upperCase')} showError={showError && !rules.upper}/>
  <RuleRow ok={rules.lower} label={t('passwordRules.lowerCase')} showError={showError && !rules.lower}/>
  <RuleRow ok={rules.number} label={t('passwordRules.number')} showError={showError && !rules.number}/>
  <RuleRow ok={rules.special} label={t('passwordRules.special')} showError={showError && !rules.special}/>
</Box>
  );
};

/* -------------------- CHANGE PASSWORD --------------------- */
const ChangePasswordSettings = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      formik.resetForm();
      setTimeout(() => {
        document.getElementById("currentPasswordInput")?.focus();
      }, 100);
    }
  }, [open]);

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validate: (values) => {
      const errors = {};
      const pwd = values.newPassword || "";

      const hasLen = pwd.length >= 8;
      const hasLower = /[a-z]/.test(pwd);
      const hasUpper = /[A-Z]/.test(pwd);
      const hasNum = /[0-9]/.test(pwd);
      const hasSpecial = /[!@#$%^&*()[\]{}\-_+=~`|:;"'<>,.?/\\]/.test(pwd);

      if (!values.currentPassword)
        errors.currentPassword = t("change_password_data.errors.current_required");

      if (!pwd) {
        errors.newPassword = t("change_password_data.errors.new_required");
      } else if (!(hasLen && hasLower && hasUpper && hasNum && hasSpecial)) {
        errors.newPassword = t("change_password_data.errors.weak");
      }

      if (!values.confirmPassword)
        errors.confirmPassword = t("change_password_data.errors.confirm_required");
      else if (values.confirmPassword !== values.newPassword)
        errors.confirmPassword = t("change_password_data.errors.not_match");

      return errors;
    },
      validateOnBlur: false,   // 👈 disable validation on blur
  validateOnChange: false, //
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        const rawEncryptedToken = localStorage.getItem("token");
        const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

        const response = await fetch(
          `${BASE_URL}/account-executive/change-password`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(values),
          }
        );

        const data = await response.json();

        if (response.ok && !data.error) {
           showToast(toast.success, data, t("alerts.changePasswordSuccess"));
          setTimeout(() => {
            onClose();
          }, 1000);
        } else {
          toast.error(data.message || t("change_password_data.failed"));
        }
      } catch (error) {
       showToast(toast.error, error, t("alerts.changePasswordError"));
      } finally {
        setIsLoading(false);
      }
    },
  });

  if (!open) return null;

  return (
    <>
    <Box>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.3)",
          zIndex: 1400,
          display: "flex",
          justifyContent: "center",
          // alignItems: "center",
          px: isMobile ? 2 : 0,
          py: isMobile ? 2 : 0,
           overflowY: "auto",
    py: 4, 
         
        }}
        onClick={onClose}
      >
        <Toaster />
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            width: "100%",
            maxWidth: 410,
            px: isMobile ? 2 : 4,
            py: 3,
            maxHeight: isMobile ? "93vh" : "auto",
            overflowY: isMobile ? "auto" : "visible",
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                  my: "auto",
          }}
        >
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ mb: 2 }}>
                <CustomText
                  sx={{ ...textStyles.h4, color: theme.palette.tertiary.main, mb: 1 }}
                >
                  {t("change_password_data.title")}
                </CustomText>
                <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary }}>
                  {t("change_password_data.description")}
                </CustomText>
              </Box>

              {/* Current password */}
              <label>{t("change_password_data.current_password")}</label>
              <CustomPasswordInput
                id="currentPasswordInput"
                name="currentPassword"
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.currentPassword && formik.errors.currentPassword}
              />
              {formik.touched.currentPassword && formik.errors.currentPassword && (
                <span style={{ color: "red", fontSize: "12px" }}>
                  {formik.errors.currentPassword}
                </span>
              )}

              {/* New password */}
              <label>{t("change_password_data.new_password")}</label>
              <CustomPasswordInput
                name="newPassword"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.newPassword && formik.errors.newPassword}
              />
              {/* {formik.touched.newPassword && formik.errors.newPassword && (
                <span style={{ color: "red", fontSize: "12px" }}>
                  {formik.errors.newPassword}
                </span>
              )} */}

              {/* 👇 Checklist appears under new password */}
              <PasswordChecklist
                password={formik.values.newPassword}
                showError={formik.touched.newPassword}
              />

              {/* Confirm password */}
              <label>{t("change_password_data.confirm_password")}</label>
              <CustomPasswordInput
                name="confirmPassword"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword && formik.errors.confirmPassword}
              />
              {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                <span style={{ color: "red", fontSize: "12px" }}>
                  {formik.errors.confirmPassword}
                </span>
              )}

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
                <CustomButton
                  label={t("common.cancel")}
                  variant="text"
                  onClick={onClose}
                  sx={{
                    color: "black",
                    px: 4,
                    backgroundColor: "white",
                    height: 20,
                    "&:hover": { backgroundColor: "#ffffff" },
                  }}
                />
                <CustomButton
                  label={
                    isLoading ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#fff" }}>
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                        {t("common.update")}
                      </Box>
                    ) : (
                      t("common.update")
                    )
                  }
                  type="submit"
                  size="medium"
                  sx={{ px: 2, height: 20 }}
                  disabled={isLoading}
                />
              </Box>
            </Box>
          </form>
        </Box>
      </Box>
      </Box>
    </>
  );
};

export default ChangePasswordSettings;